<template>
  <div class="editor">
    <tiny-mce
      :key="isCodePart ? codePartNodeIndex : `${partId}:${refleshKey}`"
      :init="initial"
      ref="tinymce"
      v-if="active"
      v-model="tinyMceBody"
      v-on:input="updateBody($event)"
    />
  </div>
</template>

<script>
import TinyMce from '@tinymce/tinymce-vue'
import changeColorMode from '@/components/Content/Palette/EditPartDesign/Editor/TinyMceEditor/ChangeColorMode'
import customLink from '@/components/Content/Palette/EditPartDesign/Editor/TinyMceEditor/CustomLink'
import customUnlink from '@/components/Content/Palette/EditPartDesign/Editor/TinyMceEditor/CustomUnlink'
import { getLinkExtractionBody, restoreLinkToBody } from '@/utils/String'

const EDITOR_DEFAULT_HEIGHT = 500
const CODE_PARTS_EDITOR_HEIGHT = 300

export default {
  components: {
    TinyMce,
  },
  deactivated () {
    // keep-aliveの不具合対応
    // https://github.com/tinymce/tinymce-vue/issues/10#issuecomment-414617414
    this.active = false
  },
  activated () {
    this.active = true
  },
  props: [
    'value',
    'isCodePart',
    'sectionId',
    'partsContainerId',
    'partId',
    'codePartNodeIndex',
    'extTypeName',
  ],
  data () {
    return {
      active: true,
      link: {
        modalObserver: null,
      },
      tinyMceBody: '',
      personalizeItems: [],
      refleshKey: 0,
    }
  },
  computed: {
    // テキストエディタのパラメータ
    // forced_root_block: ルートブロックの挿入 コードパーツのみfalse
    initial () {
      const personalizeItems = this.personalizeItems
      const [toolBarFirstLine, toolBarSecondLine] = this.initToolBar()
      const getPersonalizeItemsFunction = this.getPersonalizeItems
      return {
        content_css: `/style/reset.css,/style/content-default.css`,
        content_style: this.editorStyles(),
        selector: '.tinymceEditor',
        height: this.isCodePart ? CODE_PARTS_EDITOR_HEIGHT : EDITOR_DEFAULT_HEIGHT,
        menubar: false,
        language: 'ja',
        branding: false,
        fontsize_formats: '8px 10px 12px 14px 16px 18px 24px 36px',
        forced_root_block: !this.isCodePart,
        block_formats: 'Paragraph=p; 見出し1=h1; 見出し2=h2; 見出し3=h3; 見出し4=h4',
        plugins: [
          'paste wordcount custompluginpersonalize'
        ],
        paste_as_text: true,
        toolbar: [
          toolBarFirstLine,
          toolBarSecondLine
        ],
        target_list: [
          { text: '同じウィンドウ', value: '_top' },
          { text: '新規ウィンドウ', value: '_blank' }
        ],
        setup: function (editor) {
          changeColorMode.setup(editor)
          customLink.setup(editor)
          customUnlink.setup(editor)
          editor.on('keydown', function (event) {
            // ESCで閉じる
            if (event.key === 'Escape') {
              document.querySelector('[data-shortcut-esc="editorCloseButton"]').click()
            }
            // ctrl + s か command + s で保存
            if (event.key === 's' && ((event.ctrlKey && !event.metaKey) || (!event.ctrlKey && event.metaKey))) {
              event.preventDefault()
              document.querySelector('[data-shortcut-ctl-s="editorHeaderSaveButton"]').click()
            }
          })
          editor.on('mouseenter', function (event) {
            // ドラッグ → 書式変更が効かない場合の対応
            // テキストエリアで1アクションを加える
            const keyEvent = new KeyboardEvent('keyup', { keyCode: 38 }) // キーボードの↑
            document.querySelector('iframe').contentWindow.document.querySelector('.mce-content-body').dispatchEvent(keyEvent)
          })
          editor.ui.registry.addMenuButton('custompluginpersonalize', {
            text: 'パーソナライズ',
            fetch: function (callback) {
              const insertString = (str) => {
                editor.insertContent(str)
              }
              const items = personalizeItems.map(tag => (
                { type: 'menuitem', text: tag.name, onAction: () => insertString(tag.insert_string) }
              ))
              callback(items)
            }
          })
          // パーソナライズ更新
          editor.ui.registry.addButton('personalizeUpdate', {
            text: 'パーソナライズ更新',
            onAction: () => {
              getPersonalizeItemsFunction(true)
            },
          })
        },
      }
    },
    palette () {
      return this.$store.getters.getStatusPalette
    },
    isShowPersonalize () {
      return !this.isCodePart && ['ec_v2', 'mp_lite', 'mp_next'].includes(this.extTypeName)
    },
  },
  mounted () {
    // store のリンクをクリア
    this.$store.commit('setTinyMceHrefs', {
      hrefs: []
    })
    // body の初期値を挿入
    if (this.isCodePart) {
      this.tinyMceBody = this.value
    } else {
      let hrefsArray = []
      // リンクの&誤変換対策
      ;[this.tinyMceBody, hrefsArray] = getLinkExtractionBody(this.value)
      this.$store.commit('setTinyMceHrefs', {
        hrefs: hrefsArray
      })
    }
    // リンクモーダルのEnter保存イベントを取り除く
    // リンクモーダルを検知する準備
    this.link.modalObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.target.classList.contains('tox', 'tox-silver-sink', 'tox-tinymce-aux') &&
          mutation.addedNodes.length > 0 &&
          !mutation.previousSibling
        ) {
          // モーダル出現を検知
          const el = document.querySelector('div.tox-dialog__footer-end > button[title="保存"]')
          if (el) {
            // Enter保存のイベントを取り除く
            const inputEls = document.body.querySelectorAll('.tox-textfield')
            inputEls.forEach(el => {
              el.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                }
              })
            })
          }
          // リンクモーダルの値が存在するタブを表示するための処理
          const tabElements = [...document.querySelectorAll('div.tox-dialog__body-nav-item')].reverse()
          for (const tabElement of tabElements) {
            tabElement.click()
            tabElement.focus()
            if (this.getModalFirstInputValue() !== '') {
              break
            }
          }
        }
      })
    })
    // リンクモーダル検知開始
    const target = document.body
    this.link.modalObserver.observe(target, {
      childList: true,
      subtree: true,
    })
  },
  beforeMount () {
    this.getPersonalizeItems(false)
  },
  beforeDestroy () {
    // リンクモーダル検知終了
    this.link.modalObserver.disconnect()
  },
  watch: {
    palette () {
      // Paletteが変更された時
      // store のリンクをクリア
      this.$store.commit('setTinyMceHrefs', {
        hrefs: []
      })
      // body の初期値を挿入
      if (this.isCodePart) {
        // コードパーツの場合、リンクプラグインではリンクを変更せず
        // CodeTagEdit で先に uuid に変換しているため、そのままの値を入れる
        this.tinyMceBody = this.value
      } else {
        // リンクの&誤変換対策
        let hrefsArray = []
        ;[this.tinyMceBody, hrefsArray] = getLinkExtractionBody(this.value)
        this.$store.commit('setTinyMceHrefs', {
          hrefs: hrefsArray
        })
      }

      if (this.$refs.tinymce && this.$refs.tinymce.editor.undoManager) this.$refs.tinymce.editor.undoManager.clear()
    },
  },
  methods: {
    initToolBar () {
      let firstLine = ''
      let secondLine = ''
      if (this.isCodePart) {
        // コードパーツの場合、装飾に関わるボタンを表示しない
        firstLine = 'undo redo | fontsizeselect | forecolor backcolor | removeformat | changeColorMode'
      } else {
        firstLine = 'undo redo | bold italic underline strikethrough customLink customUnlink | alignleft aligncenter alignright alignjustify'
        secondLine = 'formatselect | fontsizeselect | forecolor backcolor | removeformat | changeColorMode'
        if (this.isShowPersonalize) {
          secondLine += ' | custompluginpersonalize | personalizeUpdate'
        }
      }
      return [firstLine, secondLine]
    },
    getPersonalizeItems ($updateFlg = false) {
      if (this.isShowPersonalize) {
        let url = `/content/ec_v2/personalize`
        if (!$updateFlg) {
          // 初回取得
          const items = this.$store.getters.getEcV2PersonalizeItems
          if (items.length > 0) {
            // 項目取得済み
            this.personalizeItems = items
            this.refleshKey++
            return
          }
        } else {
          // 更新
          url = url + '?update=1'
        }
        this.$http({
          url: url,
          type: 'GET',
        }).then(json => {
          if (json && json.status === 200 && json.data.columns) {
            this.personalizeItems = json.data.columns
            this.$store.commit('updateEcV2PersonalizeItems', this.personalizeItems)
            this.refleshKey++
          }
        })
      }
    },
    updateBody (value) {
      // リンクの&誤変換対策
      const body = restoreLinkToBody(value, this.$store.state.status.tinyMceHrefs)

      if (this.isCodePart) {
        this.$emit('change', {
          type: 'text',
          nodeIndex: this.codePartNodeIndex,
          newValue: body
        })
      } else {
        this.$store.commit('updatePartBody', {
          sectionId: this.sectionId,
          partsContainerId: this.partsContainerId,
          partId: this.partId,
          body
        })
      }
    },
    // コンテンツのスタイル
    editorStyles () {
      const { textEditorPadding, defaultLineHeight } = this.$store.getters.noUnitScssVars
      const { defaultFontFamily } = this.$store.getters.getScssVars
      const defaultStyle = `#tinymce { padding: ${textEditorPadding}px } body { line-height: ${defaultLineHeight}; font-family: ${defaultFontFamily}; }`
      return defaultStyle + this.$store.state.styles.map(style => `.mce-content-body ${JSON.parse(JSON.stringify(style.selector))} { ${JSON.parse(JSON.stringify(style.prop))}: ${JSON.parse(JSON.stringify(style.value))} }`).join('\n')
    },
    getModalFirstInputValue () {
      const targetFormGroup = document.querySelectorAll('.tox-form > .tox-form__group')[1] // [0] はリンク元テキスト
      const inputValue = targetFormGroup.querySelector('input').value
      return inputValue
    },
  }
}
</script>

<style lang="scss">
.tox-dialog__popups {
  display: none; // リンクの自動補完を表示させない
}
</style>
